import { Link } from 'react-router';
import {
  GuideLayout,
  GuideSection,
  GuideTip,
  GuideGrid,
  GuideCard,
  GuideSteps,
  GuideStep,
  GuideDarkSection,
} from '@/components/guides/guide-layout';
import {
  DFT_RATINGS_SOURCE,
  RATED_ENGLISH_AUTHORITIES,
  getDftRatingStats,
} from '@fixtyer/content';

const inlineLink =
  'text-brand-600 hover:text-brand-700 font-medium underline decoration-brand-300 underline-offset-2';

// Derived from the checked-in DfT dataset so the figures can never drift from /councils.
const DFT_RATINGS = getDftRatingStats();
const RATED_AUTHORITY_COUNT = RATED_ENGLISH_AUTHORITIES.length;

const RAC_FOI_URL =
  'https://www.rac.co.uk/drive/news/state-of-the-roads/council-pothole-compensation-claims-double-in-a-year/';

export function HowToClaimForPotholeDamageGuide() {
  return (
    <GuideLayout
      slug="how-to-claim-for-pothole-damage"
      category="Claim process"
      categoryColor="brand"
      title="How to Claim for Pothole Damage from the Council"
      seoTitle="How to Claim for Pothole Damage from the Council | Fixtyer"
      description="Step-by-step England and Wales guide to claiming pothole damage from the council: who pays, the evidence you need, typical payouts and what to do if refused."
      readTime="12 min"
      updatedDate="2026-09-13"
      datePublished="2026-09-13"
      reviewedFor="England and Wales"
      contentOwner="Elevyn Technology Group Limited editorial team"
      showCouncilDataCallout
      sources={[
        { name: 'GOV.UK: claim for damage to your vehicle', url: 'https://www.gov.uk/claim-for-damage-to-your-vehicle' },
        { name: 'Highways Act 1980 s.41', url: 'https://www.legislation.gov.uk/ukpga/1980/66/section/41' },
        { name: 'Highways Act 1980 s.58', url: 'https://www.legislation.gov.uk/ukpga/1980/66/section/58' },
        { name: 'RAC FOI analysis (2023 claims)', url: RAC_FOI_URL },
        { name: 'National Highways Red Claims', url: 'https://nationalhighways.co.uk/roads-and-travel/road-projects/network-claims/claims-against-national-highways-red-claims/' },
        { name: 'DfT road maintenance ratings 2025-26', url: DFT_RATINGS_SOURCE.url },
      ]}
      relatedGuides={[
        { slug: 'pothole-claim-letter-template', title: 'Free Pothole Claim Letter Template', readTime: '6 min' },
        { slug: 'section-58-defence', title: 'Section 58 of the Highways Act 1980', readTime: '9 min' },
        { slug: 'request-road-inspection-records', title: 'Request Road Inspection Records', readTime: '8 min' },
      ]}
      atAGlance={[
        {
          label: 'Answer first',
          value:
            'In England and Wales you claim the cost of pothole damage from the authority responsible for the road, usually your local council or National Highways, under the Highways Act 1980. You submit the claim yourself and there is no fee to claim.',
        },
        {
          label: 'What the response turns on',
          value:
            'Section 41 places a duty to maintain the road; Section 58 gives the authority a defence if it took reasonable care. Your evidence, and what the authority’s own records show it knew, decide the response.',
        },
        {
          label: 'What councils paid in 2023',
          value:
            'In the RAC’s Freedom of Information analysis, 17 councils paid 3,131 of 20,432 claims (15%) and the RAC estimated an average payout of about £260.',
        },
        {
          label: 'Cost to claim',
          value:
            'Nothing. Writing to the authority is free. Fixtyer’s optional Council Claim Pack is £35 one-off with no commission; you review and submit it yourself.',
        },
        {
          label: 'Time limit',
          value:
            'Six years from the date of damage for property claims under the Limitation Act 1980. Claim promptly anyway: evidence is stronger and the defect may still be there to photograph.',
        },
        {
          label: 'Scope',
          value:
            'England and Wales. DfT council ratings on Fixtyer cover English highway authorities only and are network context, not proof of liability for a specific incident.',
        },
      ]}
      faqs={[
        {
          question: 'Can I claim for a tyre damaged by a pothole?',
          answer:
            'Yes. Tyres and wheels are the most common pothole claims and among the easiest to evidence. Photograph the tyre on the vehicle before it is replaced, keep the itemised invoice and, if the garage allows, keep the damaged part. Expect the authority to deduct for wear if the tyre was already part-worn.',
        },
        {
          question: 'Do I need to report the pothole before I claim?',
          answer:
            'Report it. Many authorities ask for a report reference before they will open a claim, and reporting creates a dated record. Your own report does not show the authority knew about the defect before your incident; an earlier report by someone else is a lead to verify against the authority’s own records.',
        },
        {
          question: 'Can I claim for pothole damage on a motorway?',
          answer:
            'Yes, but not from the council. Motorways and major A roads in England are National Highways’ responsibility and it runs its own Red Claims process. Welsh motorways and trunk roads are the Welsh Government’s responsibility, handled through Traffic Wales. London red routes are Transport for London.',
        },
        {
          question: 'Will a pothole claim affect my car insurance?',
          answer:
            'Claiming directly from the highway authority does not go through your insurer, so it is not an insurance claim. Check your policy: some insurers require you to tell them about any incident, even one you do not claim for. If you claim on your own policy instead, you pay the excess and it may affect your premium.',
        },
        {
          question: 'What if I was driving in the dark or above the speed limit?',
          answer:
            'The authority may argue contributory negligence and reduce or refuse the claim. Record the conditions honestly, including your speed, visibility and whether the pothole could be seen in time to avoid it. A dashcam clip is the clearest way to show what a driver could reasonably have done.',
        },
        {
          question: 'Can I claim if the pothole has since been repaired?',
          answer:
            'Yes, if you have dated photographs from the time of the incident. Ask the authority for its defect and repair records for the location; a repair after your incident shows how the authority categorised the defect once it was recorded.',
        },
        {
          question: 'Does Fixtyer submit the claim for me?',
          answer:
            'No. Fixtyer prepares the claim pack, reviews your evidence and searches for possible prior reports. You send the pack to the authority and handle all replies yourself. Fixtyer is a document-preparation service, not legal advice, and it does not act on your behalf.',
        },
      ]}
    >
      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
          </svg>
        }
        title="Do councils have to pay for pothole damage?"
      >
        <p className="mb-4">
          Not automatically. A highway authority in England or Wales has a statutory duty to maintain the
          roads it is responsible for under{' '}
          <Link to="/guides/section-41-explained" className={inlineLink}>
            Section 41 of the Highways Act 1980
          </Link>
          . It is liable for pothole damage only where it breached that duty, and{' '}
          <Link to="/guides/section-58-defence" className={inlineLink}>
            Section 58
          </Link>{' '}
          gives it a defence if it can show it took the care reasonably required to keep the road safe.
        </p>
        <p className="mb-4">
          In practice the authority will look at when the road was last inspected, whether the defect was
          recorded or reported before your incident, and whether any repair was carried out within its own
          published timescale. A defect that appeared between reasonable inspections and was not reported may
          support the defence. A defect that was recorded and left may not. Neither point decides the claim on
          its own; the authority&apos;s own records for the location are what matter.
        </p>
        <GuideTip variant="info" title="Why refusals are so common">
          In the RAC&apos;s Freedom of Information analysis of 2023 claims, nine councils that gave a reason refused
          6,028 of 8,172 claims (74%) on the basis that they did not know the pothole existed. That is the
          knowledge question at the centre of Section 58, and it is why checking the authority&apos;s records for
          earlier reports is the most useful step before you write.
        </GuideTip>
      </GuideSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        }
        title="How to claim, step by step"
      >
        <p className="mb-6">
          Gather evidence, identify the responsible authority, report the pothole, submit a written claim, and
          keep every reply. The order matters because the earliest steps are the ones you cannot repeat later.
        </p>
        <GuideSteps>
          <GuideStep number={1} title="Stop safely and photograph the pothole">
            Take several angles with a tape measure, coin or shoe for scale, and one wide shot that shows road
            markings, a sign or a building so the location is unmistakable. Never stop on a motorway or
            dual carriageway to do this. See{' '}
            <Link to="/guides/photographing-pothole-damage" className={inlineLink}>
              how to photograph pothole damage
            </Link>
            .
          </GuideStep>
          <GuideStep number={2} title="Record the exact location, date and time">
            Note the road name, the nearest postcode or what3words reference, the direction you were travelling
            and the lane. Write down the time and the weather and light conditions while they are fresh.
          </GuideStep>
          <GuideStep number={3} title="Photograph the damage before it is repaired">
            Capture the tyre, wheel, suspension or bodywork on the vehicle, then again once removed. Ask the garage
            to note on the invoice that the damage is consistent with a pothole impact. See{' '}
            <Link to="/guides/documenting-vehicle-damage" className={inlineLink}>
              documenting vehicle damage
            </Link>
            .
          </GuideStep>
          <GuideStep number={4} title="Get an itemised invoice or two written quotes">
            Authorities pay against actual loss, so an estimate you wrote yourself will not do. Keep receipts for
            any recovery, and check the authority&apos;s policy before assuming a hire car will be covered. See{' '}
            <Link to="/guides/getting-repair-quotes" className={inlineLink}>
              getting the right repair quote
            </Link>
            .
          </GuideStep>
          <GuideStep number={5} title="Check for earlier reports of the same defect">
            Search FixMyStreet and the authority&apos;s own reporting tool for reports at the same location before your
            incident, and screenshot anything you find with its date. These are research leads to verify against the
            authority&apos;s records, not proof in themselves. See{' '}
            <Link to="/guides/prior-reports-explained" className={inlineLink}>
              why prior reports matter
            </Link>
            .
          </GuideStep>
          <GuideStep number={6} title="Report the pothole and keep the reference">
            Report it to the responsible authority and keep the reference number. Your own report does not show
            earlier knowledge, but many authorities require it, and it creates a dated record. See{' '}
            <Link to="/guides/fix-my-street-pothole-reporting" className={inlineLink}>
              reporting a pothole
            </Link>
            .
          </GuideStep>
          <GuideStep number={7} title="Submit the claim in writing with your evidence">
            Use the authority&apos;s claim form if it has one, or a letter citing the Highways Act 1980, with your
            photographs, invoice, location details and report reference attached. Our{' '}
            <Link to="/guides/pothole-claim-letter-template" className={inlineLink}>
              free claim letter template
            </Link>{' '}
            covers the points an authority expects to see.
          </GuideStep>
          <GuideStep number={8} title="If refused, ask for the inspection and repair records">
            Request the inspection policy, completed inspection records, defect reports and repair logs for that
            stretch of road for the 12 months before your incident, then compare them with the reasons given. See{' '}
            <Link to="/guides/request-road-inspection-records" className={inlineLink}>
              how to request road inspection records
            </Link>
            .
          </GuideStep>
        </GuideSteps>
      </GuideSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        }
        title="What evidence do you need?"
      >
        <p className="mb-4">
          You need to show four things: the defect, the damage, the cost, and the link between them. Causation is
          where many claims fall down, so anything that ties the damage to that pothole on that day is valuable.
        </p>
        <GuideGrid cols={2}>
          <GuideCard title="The defect" variant="accent">
            Dated photographs of the pothole with a depth and width measurement, plus a wide shot fixing the
            location. A dashcam clip of the impact is the strongest single item.
          </GuideCard>
          <GuideCard title="The damage">
            Photographs of the damaged part on the vehicle and after removal, and the garage&apos;s written note of
            the likely cause.
          </GuideCard>
          <GuideCard title="The cost">
            An itemised repair invoice or two written quotes, recovery receipts, and a note of any part-worn
            component so the authority&apos;s deduction for wear is fair.
          </GuideCard>
          <GuideCard title="What the authority knew">
            Your report reference, screenshots of any earlier reports at the same location and date, and later
            the authority&apos;s own inspection and repair records for the road.
          </GuideCard>
        </GuideGrid>
        <p>
          Keep everything together in one folder with a short timeline. The{' '}
          <Link to="/guides/what-makes-a-valid-claim" className={inlineLink}>
            valid claim checklist
          </Link>{' '}
          walks through each item and the common gaps.
        </p>
      </GuideSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
          </svg>
        }
        title="How deep does a pothole have to be to claim?"
      >
        <p className="mb-4">
          There is no legal minimum depth. Many English authorities use an investigatory level of around 40mm,
          following the UK Roads Leadership Group&apos;s <em>Well-managed Highway Infrastructure</em> code of practice,
          but each authority sets its own levels and some use different figures for different road types. A
          pothole below the authority&apos;s investigatory level is harder to claim for, not impossible: the depth is
          one factor among the character of the road, the traffic it carries and what a reasonable person would
          expect to find.
        </p>
        <GuideTip variant="warning" title="Measure it">
          Photograph a tape measure in the pothole showing depth and width. Without a measurement the authority
          can argue the defect was below its intervention level and you will have nothing to answer with.
        </GuideTip>
      </GuideSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        }
        title="Who is responsible for the road?"
      >
        <p className="mb-4">
          The claim goes to the highway authority for that road, which is not always the council. Sending it to
          the wrong body costs weeks.
        </p>
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-sm">
            <caption className="sr-only">Highway authorities responsible for pothole claims in England and Wales</caption>
            <thead className="bg-surface-100">
              <tr>
                <th className="text-left p-3 font-semibold text-brand-950">Road</th>
                <th className="text-left p-3 font-semibold text-brand-950">Who to claim from</th>
                <th className="text-left p-3 font-semibold text-brand-950">How</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-200">
              <tr>
                <td className="p-3 text-surface-700">Local roads in England</td>
                <td className="p-3 text-surface-700">The unitary, county, borough or district council that is the highway authority</td>
                <td className="p-3 text-surface-700">
                  Council claim form or claims team. <Link to="/councils" className={inlineLink}>Find your council</Link>
                </td>
              </tr>
              <tr>
                <td className="p-3 text-surface-700">Motorways and major A roads in England</td>
                <td className="p-3 text-surface-700">National Highways</td>
                <td className="p-3 text-surface-700">
                  Red Claims damage report form; contact centre 0300 123 5000. See the{' '}
                  <Link to="/guides/national-highways-pothole-claims" className={inlineLink}>National Highways guide</Link>
                </td>
              </tr>
              <tr>
                <td className="p-3 text-surface-700">Motorways and trunk roads in Wales</td>
                <td className="p-3 text-surface-700">Welsh Government, via Traffic Wales</td>
                <td className="p-3 text-surface-700">
                  Traffic Wales claims process; 0300 123 1213. See{' '}
                  <Link to="/guides/england-wales-pothole-claims" className={inlineLink}>England vs Wales</Link>
                </td>
              </tr>
              <tr>
                <td className="p-3 text-surface-700">Other roads in Wales</td>
                <td className="p-3 text-surface-700">The local authority</td>
                <td className="p-3 text-surface-700">Council claim form or claims team</td>
              </tr>
              <tr>
                <td className="p-3 text-surface-700">London red routes</td>
                <td className="p-3 text-surface-700">Transport for London</td>
                <td className="p-3 text-surface-700">TfL claims; 0343 222 1234</td>
              </tr>
              <tr>
                <td className="p-3 text-surface-700">Private roads, car parks, unadopted estate roads</td>
                <td className="p-3 text-surface-700">The landowner or management company</td>
                <td className="p-3 text-surface-700">Not a Highways Act claim; write to the owner</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Phone numbers are taken from GOV.UK and National Highways on 13 September 2026. The{' '}
          <Link to="/guides/who-is-responsible" className={inlineLink}>
            who is responsible guide
          </Link>{' '}
          and the{' '}
          <Link to="/guides/highway-authority-claim-routes" className={inlineLink}>
            claim routes directory
          </Link>{' '}
          go into more detail. Fixtyer&apos;s free check identifies the likely authority from the location.
        </p>
      </GuideSection>

      <GuideDarkSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
          </svg>
        }
        title="How much can you claim?"
      >
        <p className="text-brand-200 mb-4">
          You can claim the reasonable cost of repairing the damage the pothole caused, plus directly related
          costs such as recovery. You cannot claim for pre-existing wear, for the improvement you get from a new
          part replacing a worn one, or for inconvenience. National Highways says it is not its normal practice to
          pay for a hire car, and councils take a similar line.
        </p>
        <div className="grid sm:grid-cols-3 gap-4 mb-4">
          <div className="p-4 bg-brand-900/50 rounded-xl border-l-4 border-accent-500">
            <p className="text-white font-semibold text-2xl">£260</p>
            <p className="text-brand-300 text-sm">RAC estimate of the average council payout on 2023 claims</p>
          </div>
          <div className="p-4 bg-brand-900/50 rounded-xl border-l-4 border-brand-500">
            <p className="text-white font-semibold text-2xl">15%</p>
            <p className="text-brand-300 text-sm">Share of 20,432 claims that 17 councils paid in 2023</p>
          </div>
          <div className="p-4 bg-brand-900/50 rounded-xl border-l-4 border-brand-500">
            <p className="text-white font-semibold text-2xl">13 of 17</p>
            <p className="text-brand-300 text-sm">Paying councils that refused more than three quarters of claims</p>
          </div>
        </div>
        <p className="text-brand-300 text-sm">
          Source:{' '}
          <a href={RAC_FOI_URL} target="_blank" rel="noopener noreferrer" className="text-white underline">
            RAC Freedom of Information analysis
          </a>
          , 21 councils, published 2024. The average covers a tyre or a wheel refurbishment; suspension repairs
          are claimed less often and paid less often. Because claims are for actual loss, keep every receipt and
          do not round up.
        </p>
      </GuideDarkSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
        title="Is it worth claiming, and what does it cost?"
      >
        <p className="mb-4">
          It costs nothing to write to an authority, so the question is whether your evidence is strong enough to
          be worth your time. A claim with dated photographs, a measured defect, an itemised invoice and a record
          showing the authority had the defect on its books before your incident is worth submitting. A claim
          with a description and a receipt is likely to be refused under Section 58.
        </p>
        <p className="mb-4">
          Refusal rates vary widely between authorities and between years. The RAC found that 13 of the 17
          councils that paid any compensation in 2023 refused more than three quarters of claims. That is why
          this guide spends so much time on evidence and records: they are the only part of the process you
          control.
        </p>
        <GuideTip variant="success" title="What Fixtyer charges">
          The eligibility check is free. The Council Claim Pack is a one-off £35 with no commission on any
          compensation. You review the pack, submit it yourself and handle the authority&apos;s reply. Compensation is
          not guaranteed.
        </GuideTip>
      </GuideSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
          </svg>
        }
        title="Claim yourself, claims company, insurer or solicitor?"
      >
        <p className="mb-4">
          Claiming yourself is the cheapest route and the one that keeps all of any payout. The others cost money,
          a percentage, or your no-claims discount.
        </p>
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-sm">
            <caption className="sr-only">Routes for a pothole damage claim compared</caption>
            <thead className="bg-surface-100">
              <tr>
                <th className="text-left p-3 font-semibold text-brand-950">Route</th>
                <th className="text-left p-3 font-semibold text-brand-950">Upfront cost</th>
                <th className="text-left p-3 font-semibold text-brand-950">What you keep</th>
                <th className="text-left p-3 font-semibold text-brand-950">When it makes sense</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-200">
              <tr>
                <td className="p-3 text-surface-700">Claim yourself (free templates or Fixtyer pack)</td>
                <td className="p-3 text-surface-700">£0 to £35</td>
                <td className="p-3 text-surface-700">100% of any payout</td>
                <td className="p-3 text-surface-700">Any claim, especially under £1,000</td>
              </tr>
              <tr>
                <td className="p-3 text-surface-700">Claims management company</td>
                <td className="p-3 text-surface-700">Usually £0</td>
                <td className="p-3 text-surface-700">Payout minus a percentage fee</td>
                <td className="p-3 text-surface-700">You do not want to manage the correspondence</td>
              </tr>
              <tr>
                <td className="p-3 text-surface-700">Your car insurer</td>
                <td className="p-3 text-surface-700">Your excess</td>
                <td className="p-3 text-surface-700">Repair minus excess; may affect no-claims discount</td>
                <td className="p-3 text-surface-700">Damage is far above your excess and you need the car back fast</td>
              </tr>
              <tr>
                <td className="p-3 text-surface-700">Solicitor</td>
                <td className="p-3 text-surface-700">Fixed or conditional fee</td>
                <td className="p-3 text-surface-700">Payout minus fees</td>
                <td className="p-3 text-surface-700">Personal injury or high-value damage</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          The{' '}
          <Link to="/guides/council-claim-or-car-insurance" className={inlineLink}>
            council claim or car insurance guide
          </Link>{' '}
          works through the insurance decision in detail.
        </p>
      </GuideSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        }
        title="What if the council refuses your claim?"
      >
        <p className="mb-4">
          A refusal is a response, not a verdict. Ask the authority in writing for the inspection policy,
          completed inspection records, defect reports and repair logs for that stretch of road covering the 12
          months before your incident. Road-condition records are usually handled under the Environmental
          Information Regulations 2004, and the authority must respond within 20 working days. Compare the last
          inspection date and any recorded reports against the reasons in the refusal letter and the authority&apos;s
          own policy timescales. If the records do not support the refusal, resubmit with that comparison set out.
        </p>
        <p className="mb-4">
          If the authority still refuses and the sum justifies it, you can issue a claim in the small claims track
          through Money Claim Online. Court fees are set by HM Courts and Tribunals Service and scale with the
          amount claimed; check the current fee table (form EX50) before filing, and remember that a court weighs
          the same Section 58 factors the authority relied on.
        </p>
        <GuideGrid cols={3}>
          <GuideCard title="Rejected? Next steps">
            <Link to="/guides/pothole-claim-rejected-next-steps" className={inlineLink}>Work through the refusal letter</Link>
          </GuideCard>
          <GuideCard title="Common rejection reasons">
            <Link to="/guides/understanding-rejection-reasons" className={inlineLink}>What each reason means</Link>
          </GuideCard>
          <GuideCard title="Small claims court">
            <Link to="/guides/small-claims-court-guide" className={inlineLink}>Process, fees and what to expect</Link>
          </GuideCard>
        </GuideGrid>
      </GuideSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
        title="How long does a claim take, and is there a time limit?"
      >
        <p className="mb-4">
          The legal time limit for property damage is six years from the date of the damage under the Limitation
          Act 1980. Response times are set by each authority rather than by law. National Highways publishes its
          own targets: it aims to acknowledge a claim within 21 days and to decide liability within 90 days of
          acknowledging a fully completed form. Councils vary, and claim volumes peak after winter, so a claim
          submitted in March may take longer than one submitted in September.
        </p>
        <p>
          Claim as soon as you can regardless of the limit. Evidence is stronger, the pothole may still be there to
          photograph, and inspection records are easier to obtain. The{' '}
          <Link to="/guides/claim-timeline" className={inlineLink}>
            claim timeline guide
          </Link>{' '}
          sets out what to expect at each stage.
        </p>
      </GuideSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        }
        title="Which councils are rated worst for road maintenance in 2025-26?"
      >
        <p className="mb-4">
          For 2025-26 the{' '}
          <a href={DFT_RATINGS_SOURCE.url} target="_blank" rel="noopener noreferrer" className={inlineLink}>
            Department for Transport
          </a>{' '}
          rated {DFT_RATINGS.red} of England&apos;s {RATED_AUTHORITY_COUNT} highway authorities RED for local road
          maintenance, {DFT_RATINGS.amber} AMBER and {DFT_RATINGS.green} GREEN. The ratings assess how well each
          authority manages its network as a whole. They are useful context when you check an authority&apos;s
          inspection policy, but they do not establish notice or liability for a specific pothole, and they cover
          England only.
        </p>
        <p>
          Fixtyer publishes the{' '}
          <Link to="/road-maintenance-report" className={inlineLink}>
            full 2025-26 table
          </Link>
          , a{' '}
          <Link to="/councils" className={inlineLink}>
            report for each council
          </Link>{' '}
          and the{' '}
          <Link to="/data/council-road-maintenance-ratings" className={inlineLink}>
            underlying dataset
          </Link>{' '}
          under the Open Government Licence.
        </p>
      </GuideSection>
    </GuideLayout>
  );
}
